import { Modal } from '../../components/modal/modal';
import { useContext, useEffect, useState } from 'react';
import { Button } from '../../components/form/button';
import { toast } from '../../util/toasts';
import { Typography } from '../../components/typography/typography';
import { useTaxApiV1 } from '../../clients/quipli-service/tax/tax-api-v1';
import { TaxCategory } from '../../clients/quipli-service/tax/tax';
import { TenantContext } from '../../context/tenant-context';
import { QboTaxCodeSelect } from '../qbo/qbo-tax-code-select';

export const TaxCategoryModal = ({
  isOpen,
  close,
  existing,
}: {
  isOpen: boolean;
  close: (isWrite: boolean) => void;
  existing?: TaxCategory;
}) => {
  const { configuration } = useContext(TenantContext);

  let qboEnabled = false;
  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }
  // @hipaa-exclude-end

  const { createTaxCategory, updateTaxCategory } = useTaxApiV1();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [taxCategoryRequest, setTaxCategoryRequest] = useState<{
    name: string;
    description?: string;
    qboTaxCodeId?: string;
  }>();

  useEffect(() => {
    if (existing) {
      setTaxCategoryRequest({
        name: existing.name,
        description: existing.description,
        qboTaxCodeId: existing.qboTaxCodeId,
      });
    }
  }, [existing]);

  async function doSave() {
    try {
      if (!taxCategoryRequest || !taxCategoryRequest.name) {
        toast('Missing required information', 'error');
        return;
      }
      setIsSaving(true);
      if (existing) {
        await updateTaxCategory(existing.id, taxCategoryRequest);
        toast('Tax Category updated successfully');
      } else {
        await createTaxCategory(taxCategoryRequest);
        toast('Tax Category created successfully');
      }
      close(true);
      clearForm();
    } catch (e: any) {
      toast('Failed to save Tax Category', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function doClose() {
    close(false);
    clearForm();
  }

  function clearForm() {
    setTaxCategoryRequest(undefined);
  }

  return (
    <Modal
      title={`${existing ? 'Update' : 'Create'} Tax Category`}
      isOpen={isOpen}
      close={doClose}
      actions={
        <>
          <Button className="button button-secondary" disabled={isSaving} onClick={doClose}>
            Cancel
          </Button>
          <Button className="button button-yellow" disabled={isSaving || !taxCategoryRequest?.name} onClick={doSave}>
            {existing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-column gap-2">
        <div>
          <Typography type={'text-md'}>Name:</Typography>
          <input
            type="text"
            value={taxCategoryRequest?.name}
            onChange={(e) => setTaxCategoryRequest({ ...taxCategoryRequest, name: e.target.value })}
          />
        </div>
        <div>
          <Typography type={'text-md'}>Description</Typography>
          {taxCategoryRequest?.name && (
            <input
              type="text"
              value={taxCategoryRequest?.description}
              onChange={(e) => setTaxCategoryRequest({ ...taxCategoryRequest, description: e.target.value })}
            />
          )}
          {!taxCategoryRequest?.name && <input type="text" disabled />}
        </div>
        {qboEnabled && configuration?.currency !== 'USD' && (
          <div>
            <label>QuickBooks Tax Code</label>
            <QboTaxCodeSelect
              taxCodeId={taxCategoryRequest?.qboTaxCodeId}
              setTaxCodeId={(qboTaxCodeId) =>
                setTaxCategoryRequest((prev) => {
                  return { ...prev, qboTaxCodeId };
                })
              }
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
